import { MouseEvent } from 'react';

import styles from './skip-link.module.css';

export const MAIN_CONTENT_ID = 'main-content';

/**
 * "Skip to content" -- the first thing in the tab order, so a keyboard or
 * switch user can jump past the topbar to the page's content instead of
 * tabbing through the same navigation on every route.
 *
 * Focus is moved by hand rather than left to the `href="#id"` jump: browsers
 * scroll to a fragment target but only *focus* it if it is focusable, so
 * without this the next Tab would continue from the link, not from the
 * content. Each page marks its own container (`id={MAIN_CONTENT_ID}`,
 * `tabIndex={-1}`); the `<main>` fallback keeps the link useful on a route
 * that hasn't been marked.
 */
export function SkipLink({
	targetId = MAIN_CONTENT_ID,
	children = 'Skip to content',
}: SkipLinkProps) {
	function onClick(event: MouseEvent<HTMLAnchorElement>) {
		const target =
			document.getElementById(targetId) ?? document.querySelector('main');
		if (!target) {
			// Nothing to skip to: leave the default fragment jump alone
			// rather than swallowing the click.
			return;
		}
		event.preventDefault();
		if (!target.hasAttribute('tabindex')) {
			target.setAttribute('tabindex', '-1');
		}
		target.focus();
	}

	return (
		<a className={styles['skip-link']} href={`#${targetId}`} onClick={onClick}>
			{children}
		</a>
	);
}

export interface SkipLinkProps {
	/** Defaults to `MAIN_CONTENT_ID`, the id every page marks its content with. */
	targetId?: string;
	children?: string;
}
